import { useState, useRef, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";

/*
========================================
AUTO BACKEND DETECTION — NO ENV NEEDED
========================================
If your backend is deployed on the SAME domain,
this will work automatically.

Example:
Frontend: https://azesus.com
Backend:  https://azesus.com/api/ask
*/

const API =
  window.location.hostname === "localhost"
    ? "http://localhost:8000/api" // local dev backend
    : `${window.location.origin}/api`; // production backend

// Storage keys
const STORAGE_KEY = "azesus_chats_v1";
const ACTIVE_CHAT_KEY = "azesus_active_chat";

// Utility
const generateId = () =>
  `id_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

const sanitizeResponse = (text) => {
  if (!text) return "";
  return text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/^[\s]*[-•*]\s+/gm, "")
    .replace(/^[\s]*\d+\.\s+/gm, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[\d+\]/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// Logo
const AzesusLogo = ({ size = 60, isThinking = false }) => (
  <div
    className={`azesus-logo ${isThinking ? "thinking" : ""}`}
    style={{ width: size, height: size * 0.6 }}
  >
    <div className="portal">
      <div className="ring r1"></div>
      <div className="ring r2"></div>
      <div className="ring r3"></div>
      <div className="core"></div>
    </div>
  </div>
);

// Chat storage
const loadChats = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const saveChats = (chats) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(chats));

const loadActiveChat = () =>
  localStorage.getItem(ACTIVE_CHAT_KEY) || null;

const saveActiveChat = (id) =>
  id
    ? localStorage.setItem(ACTIVE_CHAT_KEY, id)
    : localStorage.removeItem(ACTIVE_CHAT_KEY);

// ===============================
// MAIN APP
// ===============================
function App() {
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [askInput, setAskInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const transcriptRef = useRef(null);

  useEffect(() => {
    const loaded = loadChats();
    setChats(loaded);

    const activeId = loadActiveChat();
    if (activeId && loaded.find((c) => c.id === activeId)) {
      setActiveChatId(activeId);
    } else if (loaded.length > 0) {
      setActiveChatId(loaded[0].id);
    }
  }, []);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop =
        transcriptRef.current.scrollHeight;
    }
  }, [chats, activeChatId]);

  const activeChat = chats.find((c) => c.id === activeChatId);

  const createNewChat = () => {
    const newChat = {
      id: generateId(),
      title: "New Chat",
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    const updated = [newChat, ...chats];
    setChats(updated);
    saveChats(updated);

    setActiveChatId(newChat.id);
    saveActiveChat(newChat.id);

    return newChat.id;
  };

  // ===============================
  // 🔥 ASK FUNCTION (FIXED)
  // ===============================
  const handleAsk = async () => {
    if (!askInput.trim() || isLoading) return;

    let chatId = activeChatId;
    if (!chatId) chatId = createNewChat();

    const question = askInput.trim();

    setIsLoading(true);
    setAskInput("");

    try {
      console.log("Calling backend:", `${API}/ask`);

      const res = await axios.post(
        `${API}/ask`,
        { query: question },
        { timeout: 120000 }
      );

      const answer =
        res?.data?.response || "No response received.";

      const cleaned = sanitizeResponse(answer);

      const newMessage = {
        id: generateId(),
        question,
        answer: cleaned,
        timestamp: Date.now(),
      };

      setChats((prev) => {
        const updated = prev.map((c) => {
          if (c.id === chatId) {
            const title =
              c.messages.length === 0
                ? question.slice(0, 40)
                : c.title;

            return {
              ...c,
              title,
              messages: [...c.messages, newMessage],
              updatedAt: Date.now(),
            };
          }
          return c;
        });

        saveChats(updated);
        return updated;
      });
    } catch (err) {
      console.error("Ask error:", err);
      alert("Backend connection failed. Check console.");
    } finally {
      setIsLoading(false);
    }
  };

  // ===============================
  // UI
  // ===============================
  return (
    <div className="azesus-app">
      <header className="mobile-header">
        <div className="mobile-brand">
          <AzesusLogo size={28} isThinking={isLoading} />
          <span>AZESUS</span>
        </div>
      </header>

      <div className="ask-content">
        {isLoading && (
          <div className="thinking-state">
            <AzesusLogo size={80} isThinking={true} />
            <p>Thinking...</p>
          </div>
        )}

        <div ref={transcriptRef} className="transcript">
          {!activeChat || activeChat.messages.length === 0 ? (
            <div className="empty-transcript">
              <AzesusLogo size={60} />
              <p>Ask a question to begin</p>
            </div>
          ) : (
            activeChat.messages.map((msg) => (
              <div key={msg.id} className="message-block">
                <div className="question-block">
                  <span>Question</span>
                  <p>{msg.question}</p>
                </div>

                <div className="answer-block">
                  <span>Answer</span>
                  <p>{msg.answer}</p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="ask-input-section">
          <textarea
            value={askInput}
            onChange={(e) => setAskInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleAsk();
              }
            }}
            placeholder="Enter your question..."
            disabled={isLoading}
          />

          <button
            onClick={handleAsk}
            disabled={isLoading || !askInput.trim()}
          >
            {isLoading ? "..." : "Ask"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;
